import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type ControlSettings, type Identity } from './api';
import { formatDateTime } from './requests';

export function SettingsPage({ userId, role }: { userId: number | null; role: Identity['role'] }) {
  if (role !== 'support_admin')
    return <div className="panel">Settings are available to Support Admins.</div>;
  return <SupportSettings key={userId ?? 'session'} userId={userId} />;
}

function SupportSettings({ userId }: { userId: number | null }) {
  const [saved, setSaved] = useState<ControlSettings | null>(null);
  const [values, setValues] = useState<ControlSettings | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError('');
    api
      .controlSettings(userId)
      .then(({ settings }) => {
        if (cancelled) return;
        setSaved(settings);
        setValues(settings);
        setStale(false);
      })
      .catch((failure: unknown) => {
        if (!cancelled)
          setError(failure instanceof Error ? failure.message : 'Settings could not load.');
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, reload]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!values || busy || stale) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const { settings } = await api.saveControlSettings(userId, values);
      setSaved(settings);
      setValues(settings);
      setMessage('Settings saved.');
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Settings could not be saved. Your changes are still shown below.',
      );
      setStale(failure instanceof ApiError && failure.status === 409);
    } finally {
      setBusy(false);
    }
  }

  const dirty = JSON.stringify(values) !== JSON.stringify(saved);
  const field = (
    key: 'tropicanaContactName' | 'tropicanaEmail' | 'supportEmail' | 'registryUrl',
    label: string,
    type: string,
    help?: string,
  ) => (
    <div className="form-field">
      <label htmlFor={`setting-${key}`}>{label}</label>
      <input
        id={`setting-${key}`}
        type={type}
        value={values?.[key] ?? ''}
        maxLength={key === 'tropicanaContactName' ? 100 : key === 'registryUrl' ? 500 : 254}
        autoComplete="off"
        aria-describedby={help ? `setting-${key}-help` : undefined}
        onChange={(event) => {
          setValues((current) => current && { ...current, [key]: event.target.value });
          setMessage('');
        }}
      />
      {help && (
        <p className="settings-help" id={`setting-${key}-help`}>
          {help}
        </p>
      )}
    </div>
  );

  return (
    <section className="settings-page" aria-labelledby="settings-title">
      <div className="requests-heading">
        <div>
          <h2 id="settings-title">Settings</h2>
          <p>
            Shared contacts and website details for the Registry. Support Admins can update these as
            they change.
          </p>
        </div>
      </div>
      <div className="notice settings-notice">
        <strong>Email sending is not connected yet.</strong> These details can be saved now. Saving
        settings or flagging a request does not send an email.
      </div>
      {error && (
        <div className="notice error" role="alert">
          {error}
          {(!values || stale) && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setMessage('');
                setReload((n) => n + 1);
              }}
            >
              {stale ? 'Discard edits and load latest settings' : 'Try again'}
            </button>
          )}
        </div>
      )}
      {message && (
        <p role="status" className="notice success">
          {message}
        </p>
      )}
      {!values ? (
        !error && <p role="status">Loading settings…</p>
      ) : (
        <form onSubmit={save} className="panel settings-form">
          <fieldset disabled={busy}>
            <legend>Tropicana review contact</legend>
            <p className="settings-help">
              The person who will receive requests flagged for Tropicana review.
            </p>
            <div className="settings-fields">
              {field('tropicanaContactName', 'Contact name', 'text')}
              {field('tropicanaEmail', 'Tropicana email', 'email')}
            </div>
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Xtreme Support</legend>
            {field(
              'supportEmail',
              'Support email',
              'email',
              'Use the team mailbox or Support representative responsible for this Registry.',
            )}
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Registry website</legend>
            {field(
              'registryUrl',
              'Public website address',
              'url',
              'The public HTTPS address for future notification links. Leave blank while testing on this computer. This does not change where the app is hosted.',
            )}
          </fieldset>
          <p className="settings-help">All fields are optional until email sending is set up.</p>
          <div className="list-actions">
            <button type="submit" className="primary" disabled={busy || !dirty || stale}>
              {busy ? 'Saving…' : 'Save settings'}
            </button>
            <button
              type="button"
              disabled={busy || !dirty}
              onClick={() => {
                setValues(saved);
                setMessage('');
              }}
            >
              Discard changes
            </button>
          </div>
          {saved?.updatedAt && (
            <p className="settings-updated">
              Last saved by {saved.updatedBy?.displayName ?? 'Support'} on{' '}
              {formatDateTime(saved.updatedAt)}.
            </p>
          )}
        </form>
      )}
    </section>
  );
}
